import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import './GlobalPayments.scss';
import { getClientCapabilities } from '@/lib/utils/browserUtils.js';
import LyriaPaymentCallbackStatus from '@/lib/constant/lyriaCallbackStatuses';

const GlobalPayments = React.forwardRef(
    (
        {
            onPaymentResult,
            onPaymentStarted,
            config,
            pspId,
            loader,
            index,
            lyriaUrl,
            token,
            paymentRequestId,
        },
        ref,
    ) => {
        const {
            sessionId,
            checkoutJsUrl,
            callbackUrl,
        } = config;

        const [isScriptLoaded, setIsScriptLoaded] = React.useState(false);
        const [isConfigured, setIsConfigured] = React.useState(false);
        const [isEmbeddedVisible, setIsEmbeddedVisible] = React.useState(false);
        const [initError, setInitError] = React.useState(null);
        const [isProcessing, setIsProcessing] = React.useState(false);

        const containerId = `globalpayments-form-${index}`;
        const embedId = `globalpayments-embed-${index}`;

        const verifyResultRef = React.useRef(null);

        const process = React.useCallback(
            async () => {
                try {
                    await axios({
                        method: 'post',
                        url: lyriaUrl + '/globalpayments/process',
                        headers: { 'Content-Type': 'application/json' },
                        data: {
                            client_capabilities: getClientCapabilities(),
                            request_token: token,
                            payment_request_id: paymentRequestId,
                            psp: pspId,
                            data: {},
                        },
                    });
                } catch (error) {
                    onPaymentResult({ success: false, data: error?.response?.data?.data || { code: 'internal' } });
                }
            },
            [lyriaUrl, onPaymentResult, paymentRequestId, pspId, token],
        );

        const verifyResult = React.useCallback(
            async (resultIndicator) => {
                setIsProcessing(true);
                try {
                    const res = await axios({
                        method: 'get',
                        url: callbackUrl,
                        params: { resultIndicator },
                        headers: { Accept: 'application/json' },
                    });

                    setIsProcessing(false);

                    if (res.data?.data?.message?.status === LyriaPaymentCallbackStatus.SUCCESS) {
                        onPaymentResult({ success: true, data: res.data?.data });
                    } else {
                        onPaymentResult({
                            success: false,
                            data: {
                                code: 'PSPError',
                                error_message: 'Payment could not be verified',
                            },
                        });
                    }
                } catch (e) {
                    setIsProcessing(false);
                    if (e.response?.status === 422 && e.response?.data?.code === 'order_is_cancelled') {
                        onPaymentResult({ success: false, data: { code: 'order_is_cancelled' } });
                    } else {
                        onPaymentResult({ success: false, data: { code: 'internal' } });
                    }
                }
            },
            [callbackUrl, onPaymentResult],
        );

        verifyResultRef.current = verifyResult;

        React.useEffect(
            () => {
                const cbKey = `gpCheckout_${index}`;

                window[`${cbKey}_complete`] = (data) => {
                    let indicator = data;
                    if (typeof data === 'object' && data !== null) {
                        indicator = data.resultIndicator || data;
                    }
                    if (typeof indicator === 'string' && indicator.includes('resultIndicator=')) {
                        const params = new URLSearchParams(indicator.split('?')[1] || indicator);
                        indicator = params.get('resultIndicator') || indicator;
                    }
                    verifyResultRef.current(indicator);
                };
                window[`${cbKey}_error`] = (error) => {
                    setIsProcessing(false);
                    onPaymentResult({
                        success: false,
                        data: {
                            code: 'PSPError',
                            error_message: error?.message || 'Payment error',
                            psp: 'globalpayments',
                            pspId,
                        },
                    });
                };
                window[`${cbKey}_cancel`] = () => {
                    setIsProcessing(false);
                    setIsEmbeddedVisible(false);
                    onPaymentResult({
                        success: false,
                        data: {
                            code: 'cancelled',
                            error_message: 'Payment cancelled',
                        },
                    });
                };

                return () => {
                    delete window[`${cbKey}_complete`];
                    delete window[`${cbKey}_error`];
                    delete window[`${cbKey}_cancel`];
                };
            },
            [index, onPaymentResult, pspId],
        );

        React.useEffect(
            () => {
                if (!sessionId || !checkoutJsUrl) return;

                const scriptId = `globalpayments-script-${index}`;
                if (document.getElementById(scriptId)) {
                    setIsScriptLoaded(true);
                    return;
                }

                const cbKey = `gpCheckout_${index}`;
                const script = document.createElement('script');
                script.src = checkoutJsUrl;
                script.id = scriptId;
                script.async = true;
                script.setAttribute('data-error', `${cbKey}_error`);
                script.setAttribute('data-cancel', `${cbKey}_cancel`);
                script.setAttribute('data-complete', `${cbKey}_complete`);
                script.onload = () => setIsScriptLoaded(true);
                script.onerror = () => setInitError('Failed to load payment checkout');
                document.body.appendChild(script);

                return () => {
                    const el = document.getElementById(scriptId);
                    if (el) document.body.removeChild(el);
                };
            },
            [checkoutJsUrl, sessionId, index],
        );

        React.useEffect(
            () => {
                if (!isEmbeddedVisible) return;
                process();
            },
            [isEmbeddedVisible, process],
        );

        React.useEffect(
            () => {
                if (!isScriptLoaded || !window.Checkout || isConfigured) return;
                if (!sessionId) return;

                try {
                    window.Checkout.configure({
                        session: { id: sessionId },
                    });
                    setIsConfigured(true);
                } catch (e) {
                    setInitError('Failed to configure checkout');
                    onPaymentResult({
                        success: false,
                        data: {
                            code: 'initializationError',
                            error_message: e.message || 'Checkout configuration failed',
                            psp: 'globalpayments',
                            pspId,
                        },
                    });
                }
            },
            [isScriptLoaded, isConfigured, sessionId, pspId, onPaymentResult],
        );

        const showEmbedded = React.useCallback(
            () => {
                if (!isConfigured || !window.Checkout || isEmbeddedVisible) return;
                const embedTarget = document.getElementById(embedId);
                if (!embedTarget) return;
                onPaymentStarted();
                window.Checkout.showEmbeddedPage(`#${embedId}`);
                setIsEmbeddedVisible(true);
            },
            [isConfigured, isEmbeddedVisible, embedId, onPaymentStarted],
        );

        React.useEffect(
            () => {
                if (!isConfigured || !window.Checkout || isEmbeddedVisible) return;
                showEmbedded();
            },
            [isConfigured, isEmbeddedVisible, showEmbedded],
        );

        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    showEmbedded();
                    return true;
                },
                canClickOnPay: isConfigured && !isProcessing,
            }),
            [showEmbedded, isConfigured, isProcessing],
        );

        return (
            <div id={containerId} className="globalpayments-form">
                {initError && (
                    <div className="globalpayments-init-error">{initError}</div>
                )}

                {isProcessing && !isEmbeddedVisible && (
                    <div className="globalpayments-3ds-overlay">
                        <div className="globalpayments-3ds-spinner">
                            <div className="globalpayments-spinner-icon" />
                            <span>Processing payment...</span>
                        </div>
                    </div>
                )}

                {!initError && !isConfigured && loader}

                <div
                    id={embedId}
                    className={`globalpayments-embed ${!isConfigured ? 'globalpayments-hidden' : ''}`}
                />
            </div>
        );
    },
);

GlobalPayments.propTypes = {
    config: PropTypes.shape({
        sessionId: PropTypes.string.isRequired,
        checkoutJsUrl: PropTypes.string.isRequired,
        callbackUrl: PropTypes.string.isRequired,
    }).isRequired,
    pspId: PropTypes.number.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    onPaymentStarted: PropTypes.func.isRequired,
    loader: PropTypes.node,
    index: PropTypes.number,
    lyriaUrl: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    paymentRequestId: PropTypes.number,
};

GlobalPayments.defaultProps = {
    loader: null,
    index: 0,
};

GlobalPayments.displayName = 'GlobalPayments';

export default GlobalPayments;
